import {apiDelete, apiGet, apiPatch, apiPost, apiPut} from "../../shared/api/client";

const MULTIPART_CONFIG = {headers: {"Content-Type": "multipart/form-data"}};

const mutationPayload = (payload = {}, {omitPassword = false} = {}) => Object.fromEntries(
    Object.entries(payload).filter(([key]) => (
        key !== "countryScope"
        && key !== "country_id"
        && (!omitPassword || key !== "password")
    )),
);

const cleanParams = (filters) =>
    Object.fromEntries(Object.entries(filters || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined));

export const DEFAULT_PLACES_PAGE_SIZE = 20;

export const loadPlaces = async (filters = {}) => {
    const params = cleanParams(filters);
    // Дефолтный лимит не отправляем: поведение для существующих клиентов
    // не меняется, а queryKey остается совместимым с прежними запросами.
    if (Number(params.page_size) === DEFAULT_PLACES_PAGE_SIZE) {
        delete params.page_size;
    }
    return apiGet("/api/v2/admin/places", {params});
};

export const loadPlaceProperties = async () => apiGet("/api/v2/admin/properties/list");

export const loadPlaceOperationsSummary = async () => apiGet("/api/v2/admin/places-summary/operations");

export const loadPlace = async (placeId) => apiGet(`/api/v2/admin/places/${placeId}`);

export const loadBranch = async (branchId) => apiGet(`/api/v2/admin/places/branches/${branchId}`);

export const createPlace = async (formData) => apiPost("/api/v2/admin/places", formData, MULTIPART_CONFIG);

export const updatePlace = async (placeId, formData) =>
    apiPost(`/api/v2/admin/places/${placeId}`, formData, MULTIPART_CONFIG);

export const deletePlace = async (placeId) => apiDelete(`/api/v2/admin/places/${placeId}`);

export const rotatePlaceCredential = async (placeId, password) =>
    apiPost(`/api/v2/admin/places/${placeId}/credentials/rotate`, {password});

export const createBranch = async (placeId, payload) =>
    apiPost(`/api/v2/admin/places/${placeId}/branches`, mutationPayload(payload));

export const updateBranch = async (branchId, payload) =>
    apiPost(`/api/v2/admin/places/branches/${branchId}`, mutationPayload(payload, {omitPassword: true}));

export const deleteBranch = async (placeId, branchId) =>
    apiDelete(`/api/v2/admin/places/${placeId}/branches/${branchId}`);

export const rotateBranchCredential = async (branchId, password) =>
    apiPost(`/api/v2/admin/places/branches/${branchId}/credentials/rotate`, {password});

export const uploadBranchGalleryPhoto = async ({branchId, photo}) => {
    const formData = new FormData();
    formData.append("photo", photo);
    return apiPost(`/api/v2/admin/places/branches/${branchId}/gallery`, formData, {
        headers: {"Content-Type": "multipart/form-data"},
    });
};

export const deleteBranchGalleryPhoto = async ({branchId, galleryId}) =>
    apiDelete(`/api/v2/admin/places/branches/${branchId}/gallery/${galleryId}`);

export const loadStaffProperties = async () => apiGet("/api/v2/admin/staff/properties");

export const createStaffMember = async (payload) => apiPost("/api/v2/admin/staff", mutationPayload(payload));

export const loadBranchStaff = async (branchId) => apiGet(`/api/v2/admin/staff/branches/${branchId}`);

export const loadBranchStaffStatistics = async (branchId, days) =>
    apiGet(`/api/v2/admin/staff/branches/${branchId}/statistics`, {params: {days}});

export const loadStaffMember = async (personalId) => apiGet(`/api/v2/admin/staff/${personalId}`);

export const loadStaffMemberStatistics = async (personalId, days) =>
    apiGet(`/api/v2/admin/staff/${personalId}/statistics`, {params: {days}});

export const updateStaffMember = async (personalId, payload) => {
    return apiPost(`/api/v2/admin/staff/${personalId}`, mutationPayload(payload, {omitPassword: true}));
};

export const uploadStaffAvatar = async (personalId, avatar) => {
    const formData = new FormData();
    formData.append("avatar", avatar);
    return apiPost(`/api/v2/admin/staff/${personalId}/avatar`, formData, MULTIPART_CONFIG);
};

export const rotateStaffCredential = async (personalId, password) =>
    apiPost(`/api/v2/admin/staff/${personalId}/credentials/rotate`, {password});

export const deleteStaffMember = async (personalId, branchId) =>
    apiDelete(`/api/v2/admin/staff/${personalId}`, {params: {branch_id: branchId}});

const inviterConfig = (inviterPersonalId) => (
    inviterPersonalId === null || inviterPersonalId === undefined
        ? undefined
        : {params: {inviter_personal_id: inviterPersonalId}}
);

const referralMutationConfig = (inviterPersonalId, revision) => {
    const params = cleanParams({
        inviter_personal_id: inviterPersonalId,
        revision,
    });
    return Object.keys(params).length > 0 ? {params} : undefined;
};

export const loadReferralSettings = async (branchId) =>
    apiGet(`/api/v2/admin/staff/ref-settings/${branchId}/list`);

export const getReferralSettings = async (branchId, inviterPersonalId = null) => {
    const url = `/api/v2/admin/staff/ref-settings/${branchId}`;
    const config = inviterConfig(inviterPersonalId);
    return config ? apiGet(url, config) : apiGet(url);
};

export const updateReferralSettings = async (branchId, payload, inviterPersonalId = null) => {
    const url = `/api/v2/admin/staff/ref-settings/${branchId}`;
    const config = inviterConfig(inviterPersonalId);
    const body = mutationPayload(payload);
    return config ? apiPut(url, body, config) : apiPut(url, body);
};

export const deleteReferralSettings = async (branchId, inviterPersonalId = null, revision = null) => {
    const url = `/api/v2/admin/staff/ref-settings/${branchId}`;
    const config = referralMutationConfig(inviterPersonalId, revision);
    return config ? apiDelete(url, config) : apiDelete(url);
};

export const quickSetupReferralSettings = async (branchId, payload, inviterPersonalId = null) => {
    const url = `/api/v2/admin/staff/ref-settings/${branchId}/quick-setup`;
    const config = inviterConfig(inviterPersonalId);
    const body = mutationPayload(payload);
    return config ? apiPost(url, body, config) : apiPost(url, body);
};

export const loadReferralShares = async (branchId, inviterPersonalId = null) => {
    const url = `/api/v2/admin/staff/ref-settings/${branchId}/shares`;
    const config = inviterConfig(inviterPersonalId);
    return config ? apiGet(url, config) : apiGet(url);
};

export const createReferralShare = async (branchId, payload, inviterPersonalId = null) => {
    const url = `/api/v2/admin/staff/ref-settings/${branchId}/shares`;
    const config = inviterConfig(inviterPersonalId);
    const body = mutationPayload(payload);
    return config ? apiPost(url, body, config) : apiPost(url, body);
};

export const getReferralShare = async (branchId, shareId, inviterPersonalId = null) => {
    const url = `/api/v2/admin/staff/ref-settings/${branchId}/shares/${shareId}`;
    const config = inviterConfig(inviterPersonalId);
    return config ? apiGet(url, config) : apiGet(url);
};

export const updateReferralShare = async (branchId, shareId, payload, inviterPersonalId = null) => {
    const url = `/api/v2/admin/staff/ref-settings/${branchId}/shares/${shareId}`;
    const config = inviterConfig(inviterPersonalId);
    const body = mutationPayload(payload);
    return config ? apiPatch(url, body, config) : apiPatch(url, body);
};

export const deleteReferralShare = async (
    branchId,
    shareId,
    inviterPersonalId = null,
    revision = null,
) => {
    const url = `/api/v2/admin/staff/ref-settings/${branchId}/shares/${shareId}`;
    const config = referralMutationConfig(inviterPersonalId, revision);
    return config ? apiDelete(url, config) : apiDelete(url);
};

export const createCity = async (payload) =>
    apiPost("/api/v2/admin/properties/cities/create", mutationPayload(payload));

export const createCategory = async (payload) =>
    apiPost("/api/v2/admin/properties/categories/create", mutationPayload(payload));

export const loadPropertyCountries = async () =>
    apiGet("/api/v2/admin/properties/countries/list");

export const loadPlaceOrdering = async ({countryId, cityId}) =>
    apiGet("/api/v2/admin/place-by-position", {
        params: {
            ...(countryId == null ? {} : {country_id: countryId}),
            city_id: cityId,
        },
    });

export const savePlaceOrdering = async ({countryId, cityId, places}) =>
    apiPost(
        "/api/v2/admin/change-place-position",
        {places, city_id: Number(cityId)},
        {
            params: {
                ...(countryId == null ? {} : {country_id: countryId}),
                city_id: cityId,
            },
        },
    );
